import Dropdown from 'core-system/Dropdown'
import FlexContainer from 'core-system/FlexContainer'
import Loading from 'core-system/Loading'
import Text from 'core-system/Text'
import pxToRem from 'core-system/utils/pxToRem'
import React from 'react'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import { MyCommuterDetails } from 'redux/employee/employeeTypes'
import { Worksite } from 'redux/employer/employerTypes'
import styled from 'styled-components'
import RandomUtils from 'shared/RandomUtils'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import PrimaryButton from 'core-system/Button/PrimaryButton'

const Container = styled(FlexContainer)`
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`

const ContentWrapper = styled(FlexContainer)`
  flex-direction: column;
  padding-bottom: 0.5rem;
  width: 100%;
`

const CardOptionsContainer = styled.div`
  margin-top: 1rem;
  padding: 0.875rem 1rem;
  border: 1px solid ${(props) => props.theme.palette.grey.grey200};
  border-radius: 0.5rem;
  background-color: ${(props) => props.theme.palette.grey.grey100};
`

const CardDescription = styled(Text)`
  color: ${(props) => props.theme.palette.text.secondary};
  line-height: 1.45;
`

const NoteText = styled(Text)`
  color: ${(props) => props.theme.palette.accent.teal500};
  display: flex;
`

const CardOption = styled.div`
  margin-bottom: 0.375rem;
  color: ${(props) => props.theme.palette.text.primary};
  ${(props) => props.theme.typography.action3};
`

const UnderlinedText = styled.span`
  text-decoration: underline;
  font-weight: 700;
`

const getWorksiteDropdownItems = (
  worksites: Worksite[],
  commuterDetails: MyCommuterDetails
) => {
  return worksites.map((w) => {
    return {
      id: w.id,
      text: w.alias,
      disabled: w.id === commuterDetails.worksiteId,
    }
  })
}

interface MyCommuterChangeWorksiteProps {
  commuterDetails: MyCommuterDetails
  selectedWorksiteId: string
  onWorksiteIdChange: (worksiteId: string) => void
  onCancel: () => void
  onChangeWorksite: () => void
  isChangeWorksiteDisabled: boolean
  changeWorksiteText: string
}

const MyCommuterChangeWorksite = React.memo(
  (props: MyCommuterChangeWorksiteProps) => {
    const {
      commuterDetails,
      selectedWorksiteId,
      onWorksiteIdChange,
      onCancel,
      onChangeWorksite,
      isChangeWorksiteDisabled,
      changeWorksiteText,
    } = props

    const { worksites, payrollProviderId } = useSelector((state: AppState) => {
      return {
        worksites: state.employer.worksites,
        payrollProviderId: state.employer.profile.payrollProviderId,
      }
    })

    const currentWorksite = worksites?.find(
      (worksite) => worksite.id === commuterDetails.worksiteId
    )

    const selectableWorksites = worksites?.filter(
      (worksite) =>
        !worksite.finchWorksite &&
        !worksite.archived &&
        worksite.id !== currentWorksite?.id
    )

    const employeeWorksiteChangeFlag = useSelector(
      (state: AppState) => state.employee.employeeWorksiteChangeFlag
    )

    return (
      <>
        <Container>
          {employeeWorksiteChangeFlag === 'loading' ? (
            <Loading height={pxToRem(698)} isCard={false} />
          ) : (
            <ContentWrapper>
              <Text variant='h5' marginBottom='2rem'>
                Current Worksite:{' '}
                {currentWorksite ? currentWorksite.alias : 'None'}
              </Text>
              <Text variant='action4' style={{ marginBottom: '0.5rem' }}>
                Select a New Worksite:
              </Text>
              <Dropdown
                items={getWorksiteDropdownItems(
                  selectableWorksites,
                  commuterDetails
                )}
                active={selectedWorksiteId}
                itemCallback={({ id }) => onWorksiteIdChange(id)}
                width='100%'
                placeholder={
                  selectableWorksites.length > 0
                    ? 'Select a Worksite'
                    : 'No Worksites Available'
                }
                marginBottom='0.5rem'
              />
              <NoteText variant='action4' style={{ marginBottom: '0.25rem' }}>
                Note: Commuters may not be moved to worksites imported through
                {!payrollProviderId || payrollProviderId === ''
                  ? ' a payroll provider'
                  : ` ${RandomUtils.formatFinchProviderName(payrollProviderId)}`}
              </NoteText>
              <CardOptionsContainer>
                <CardOption>Instant Change</CardOption>
                <CardDescription variant='body2'>
                  The commuter will <UnderlinedText>instantly</UnderlinedText>{' '}
                  {commuterDetails.worksiteId
                    ? "lose access to the current worksite's products and gain access to the new worksite's products."
                    : "gain access to the new worksite's products."}
                </CardDescription>
              </CardOptionsContainer>
            </ContentWrapper>
          )}
        </Container>
        <FlexContainer justifyContent='flex-end' marginTop='2rem'>
          <SecondaryButton onClick={onCancel} style={{ marginRight: '1rem' }}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={onChangeWorksite}
            disabled={isChangeWorksiteDisabled}
          >
            {changeWorksiteText}
          </PrimaryButton>
        </FlexContainer>
      </>
    )
  }
)

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  MyCommuterChangeWorksite.displayName = 'MyCommuterChangeWorksite'
}

export default MyCommuterChangeWorksite
